import React, { useEffect, useState } from 'react';

import Confetti from 'react-confetti';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import Switch from '@components/common/Switch/Switch';
import Text from '@components/common/Text/Text';
import greetingCardService from '@services/domain/Lpc/GreetingCardService';

import css from './Customize.module.scss';

type CustomizeFormProps = {
    isVisible: boolean;
    onHide: () => void;
    potRef: string;
    hasDarkThemedGreetingCard: boolean;
    hasAnimatedGreetingCardPO: boolean;
    potCategory: string;
};

const CustomizeForm = ({
    isVisible,
    onHide,
    potRef,
    hasDarkThemedGreetingCard,
    hasAnimatedGreetingCardPO,
    potCategory,
}: CustomizeFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);
    const [withConfetti, setWithConfetti] = useState<boolean>(false);

    const onSubmit = () => {
        setIsLoading(true);
        const body = {
            disableGreetingCardPO: false,
            hasDarkThemedGreetingCard: isDarkTheme,
            hasAnimatedGreetingCardPO: withConfetti,
        };
        return greetingCardService
            .update(potRef, body)
            .then(() =>
                addToast(t(`success.UPDATE_SUCCESS`), {
                    appearance: 'success',
                    autoDismiss: true,
                }),
            )
            .catch((err) => {
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            })
            .finally(() => {
                onHide();
                setIsLoading(false);
            });
    };

    const onCancel = () => {
        setWithConfetti(hasAnimatedGreetingCardPO);
        setIsDarkTheme(hasDarkThemedGreetingCard);
        onHide();
    };

    useEffect(() => {
        setWithConfetti(hasAnimatedGreetingCardPO);
    }, [hasAnimatedGreetingCardPO]);

    useEffect(() => {
        setIsDarkTheme(hasDarkThemedGreetingCard);
    }, [hasDarkThemedGreetingCard]);

    const [recycle, setRecycle] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setRecycle(false), 4000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <Modal visible={isVisible} onHide={onHide} customClass={css.custom}>
            <div className={css.header}>
                <h5>
                    {t('greeting-card.customize.title', {
                        type: potCategory !== 'BEREAVEMENT' ? 'voeux' : 'soutien',
                    })}
                </h5>
                <Text variant={'body_02'} color={'txt-secondary'}>
                    {t('greeting-card.customize.desc', {
                        type: potCategory !== 'BEREAVEMENT' ? 'voeux' : 'soutien',
                    })}
                </Text>
            </div>

            <div className={css.form}>
                <div className={css.form__content}>
                    <div className={css.form__switch}>
                        <Text variant={'caption_01'} color={'txt-primary'}>
                            {t('greeting-card.customize.dark-theme-label')}
                        </Text>
                        <Switch
                            customClass={css.side__nav__tag__switch}
                            defaultOn={isDarkTheme}
                            onChange={(bool) => setIsDarkTheme(bool)}
                        />
                    </div>
                    {potCategory !== 'BEREAVEMENT' && (
                        <div className={css.form__switch}>
                            <Text variant={'caption_01'} color={'txt-primary'}>
                                {t('greeting-card.customize.confetti-label')}
                            </Text>
                            <Switch
                                customClass={css.side__nav__tag__switch}
                                defaultOn={withConfetti}
                                onChange={(bool) => setWithConfetti(bool)}
                            />
                        </div>
                    )}

                    <div className={`${css.form__preview} ${isDarkTheme && css.form__preview__dark} `}>
                        {withConfetti && (
                            <div className={css.form__preview__confetti}>
                                <Confetti
                                    colors={['#E22B76', '#00A5E2', '#FAC700']}
                                    width={410}
                                    height={230}
                                    recycle={recycle}
                                />
                            </div>
                        )}
                    </div>

                    {/* SUBMIT */}
                    <div className={css.form__submit}>
                        <Button
                            type={'button'}
                            customClass={css.form__submit__cta}
                            variant={'secondary'}
                            onClick={() => onCancel()}
                        >
                            {t('greeting-card.customize.cancel')}
                        </Button>
                        <Button
                            onClick={onSubmit}
                            margin={'0 0 0 8px'}
                            variant={'primary'}
                            customClass={css.form__submit__cta}
                            isLoading={isLoading}
                        >
                            {t('greeting-card.customize.confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CustomizeForm;
